'use client';

import { useState } from 'react';
import { Download, CheckCircle2, Loader2, ExternalLink } from 'lucide-react';
import { trackLeadFormSubmission, trackFormStart } from '@/lib/analytics';
import { setLeadSubmittedCookie } from '@/lib/utils/lead-cookie';
import type { LeadMagnetAsset } from '@/lib/data/lead-magnets';

export type { LeadMagnetAsset };

export interface InlineLeadMagnetProps {
  /** Headline text */
  headline: string;
  /** Supporting text below headline */
  subtext: string;
  /** The asset to gate behind email capture */
  asset: LeadMagnetAsset;
  /** Page slug for tracking (src=inline-magnet, id=pageSlug) */
  pageSlug: string;
  /** Category for tracking */
  category?: string;
  /** Lead magnet key for nurture sequence enrollment */
  magnetKey?: string;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function InlineLeadMagnet({
  headline,
  subtext,
  asset,
  pageSlug,
  category,
  magnetKey,
}: InlineLeadMagnetProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  const handleFormFocus = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('Inline Lead Magnet', pageSlug);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (honeypot) {
      setStatus('success');
      return;
    }

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setErrorMessage('Please enter your name');
      return;
    }

    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const nameParts = trimmedName.split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: trimmedEmail,
          message: `Lead magnet download: ${asset.name}\nSource: ${pageSlug}`,
          formName: `Inline Lead Magnet - ${asset.name}`,
          src: 'inline-magnet',
          id: pageSlug,
          cat: category,
          magnetKey,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit');
      }

      trackLeadFormSubmission(`Inline Lead Magnet - ${asset.name}`, pageSlug, undefined, trimmedEmail);
      setLeadSubmittedCookie();
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="my-10 rounded-xl border-2 border-green-200 bg-green-50 p-8 not-prose">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">You&apos;re all set!</h3>
          <p className="text-gray-600 mb-6">
            Here&apos;s your download. Bookmark it — it&apos;s updated regularly.
          </p>
          <a
            href={asset.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 py-3 px-6 bg-[#2813e8] hover:bg-[#1f0fb8] !text-white font-semibold rounded-lg transition-colors !no-underline"
          >
            <ExternalLink className="w-5 h-5" />
            {asset.linkLabel || `Get the ${asset.name}`}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="my-10 rounded-xl bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] p-8 text-white not-prose">
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-3 py-1 text-sm font-medium mb-3">
            <Download className="w-4 h-4" />
            Free Download
          </div>
          <h3 className="text-xl md:text-2xl font-bold mb-2">{headline}</h3>
          <p className="text-white/90 text-sm md:text-base leading-relaxed mb-1">{subtext}</p>
          <p className="text-white/70 text-sm">
            <strong>{asset.name}</strong> — {asset.description}
          </p>
        </div>

        {/* Form */}
        <div className="w-full md:w-80 flex-shrink-0">
          <form onSubmit={handleSubmit} name={`Inline Lead Magnet - ${asset.name}`} className="space-y-3">
            {/* Honeypot */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                aria-label="Leave blank"
              />
            </div>

            <input
              type="text"
              aria-label="Your name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              onFocus={handleFormFocus}
              placeholder="Your name"
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:ring-2 focus:ring-white/50 focus:border-transparent transition-colors"
            />
            <input
              type="email"
              aria-label="Work email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              onFocus={handleFormFocus}
              placeholder="Work email"
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:ring-2 focus:ring-white/50 focus:border-transparent transition-colors"
            />

            {errorMessage && (
              <p className="text-red-200 text-sm" role="alert">{errorMessage}</p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full py-3 px-4 bg-white text-[#2813e8] font-semibold rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Get Free Download
                </>
              )}
            </button>

            <p className="text-white/50 text-xs text-center">
              No spam. Unsubscribe anytime.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
